import { Injectable } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  Resolve,
  RouterStateSnapshot,
} from '@angular/router';
import { Observable, of } from 'rxjs';
import { catchError } from 'rxjs/operators';

import { Page, ProinfraBasisPageService } from '@profi/proinfra-shared-angular';

/**
 * Resolver that retrieves page-specific data before a route is activated.
 *
 * This resolver uses {@link ProinfraBasisPageService} to load page content based
 * on the current route URL. If an error occurs during the request, it logs the error
 * and returns `null`, allowing the route to continue without data.
 *
 * This class is provided in the application root and configured in the router.
 */
@Injectable({
  providedIn: 'root',
})
export class PageDataResolver implements Resolve<Page | null> {
  /**
   * Creates an instance of PageDataResolver.
   *
   * @param pageService Service for retrieving page data from the backend.
   */
  constructor(private readonly pageService: ProinfraBasisPageService) {}

  /**
   * Resolves page data before the route is activated.
   *
   * Invoked automatically by the Angular router. This method extracts the current route URL
   * (excluding query parameters) and requests the corresponding page data.
   *
   * If the request fails, it logs the error and returns `null`.
   *
   * @param route The snapshot of the route being resolved. (Unused but required by interface.)
   * @param state The current router state, from which the URL is derived.
   * @returns An observable that emits the resolved {@link Page} or `null` if an error occurs.
   */
  resolve(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<Page | null> {
    const baseRoute = state.url.split('?')[0];

    return this.pageService.getPageData(baseRoute).pipe(
      catchError(error => {
        console.error('Failed to fetch page data:', error);
        return of(null);
      })
    );
  }
}
